import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

type UseInfiniteScrollSentinelParams = {
  fetchNextPage: () => unknown;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  rootMargin?: string;
};

/**
 * Loads the next page of an infinite query when a sentinel element scrolls
 * into view. Attach the returned ref to an element rendered after the list,
 * inside the scrollable container (the sentinel's parent is used as the
 * observer root). Because the observer fires whenever the sentinel is
 * visible, pages keep loading until the list overflows the container.
 */
function useInfiniteScrollSentinel({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  rootMargin = '200px',
}: UseInfiniteScrollSentinelParams): RefObject<HTMLDivElement | null> {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (records) => {
        if (records.some((record) => record.isIntersecting)) {
          fetchNextPage();
        }
      },
      { root: sentinel.parentElement, rootMargin },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, rootMargin]);

  return sentinelRef;
}

export default useInfiniteScrollSentinel;
